import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Input, Skeleton, Spin } from 'antd';
import { debounce, isEmpty } from 'lodash';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import {
  Tabs,
  TabPane,
  PageContentPlaceholder,
  Button,
  StopoverCard,
  StopoverCardWrapper,
  List,
  ScreenLayout,
} from '../..';
import { useLazyGetChargeByIdQuery } from '../../../services/chargeApi';
import {
  StopoverResponse,
  useLazyGetStopoverGroupbyOperationByIdQuery,
  useLazyGetStopoverQuery,
  useLazyGetStorageGroupbyOperationByIdQuery,
} from '../../../services/stopoverApi';
import {
  FilterDefaultLayoutType,
  Stopover as StopoverType,
} from '../../../types';
import { formatStopoverToForm } from '../../../utils/formatters';
import { removeSpecialCharacters } from '../../../utils/utils';
import {
  FormWrapper,
  LayoutContent,
  LeftContent,
  RightContent,
} from './styles';

type DefaultLayoutProps = {
  tabs: { label: string; value: string }[];
  title: string;
  children: ReactNode;
  filterField: 'status' | 'operation_status';
  defaultActiveKey?: string;
  stopoveresData?: StopoverResponse;
  selectedStopover: StopoverType;
  setSelectedStopover: (stopover: StopoverType) => void;
  onSelectStopover?: (stopover: StopoverType) => void;
  isLoadingStopoveresData: boolean;
  cardRenderer?: (stopover: StopoverType) => ReactNode;
  fixedCardRenderer?: ReactNode;
  subTitle?: string;
  visibleAddDataDrawer?: boolean;
  setVisibleAddDataDrawer?: (value: boolean) => void;
  labelAddDataDrawer?: string;
  collapsibleCard?: boolean;
  changeContentTrigger?: 'SELECT_CARD' | 'SELECT_SUBITEM';
  selectedSubItemKey?: string | null;
  setSelectedSubItemKey?: (
    operationKey?: string | null,
    dockingIndex?: string | null
  ) => void;
  pageContext?:
    | 'STOPOVER'
    | 'STAYMENT'
    | 'OPERATION'
    | 'OPERATOR_AREA'
    | 'INVOICING'
    | 'STORAGE';
  labelPlural: string;
  queryPage: number;
  onChangePagination: (newPage?: number | undefined) => number;
  setFilter?: Dispatch<SetStateAction<FilterDefaultLayoutType>>;
  isFetchingStopoveresData?: boolean;
  // Verdadeiro quando a resposta que está na tela é do argumento pedido agora, e
  // falso quando ela ficou de um pedido anterior. É o `currentData` do hook, que
  // some quando o argumento muda e volta quando a resposta nova chega, enquanto o
  // `data` retém a anterior. Sem isso não há como saber, daqui, se a lista
  // exibida responde ao filtro atual.
  hasListDataForCurrentFilter: boolean;
  inputSearchPlaceholder?: string;
  // Tamanho de uma página da lista, que tem que ser o mesmo padrão do endpoint
  // que a serve. A rolagem cresce a janela pedida em múltiplos dele.
  pageSize: number;
  // Teto da janela, em itens, e tem que ser o mesmo teto que o endpoint aplica.
  // Se o cliente pedir acima do teto do servidor, a resposta vem cortada sem ele
  // saber, o comprimento da lista para de crescer e a rolagem trava. Obrigatório
  // de propósito, como o pageSize: um padrão silencioso aqui reproduz exatamente
  // esse travamento na tela que esquecer de passar a prop.
  maxListWindow: number;
};

const { Item: ListItem } = List;

export function DefaultLayout(props: DefaultLayoutProps) {
  const {
    children,
    tabs,
    title,
    filterField,
    defaultActiveKey,
    stopoveresData,
    isLoadingStopoveresData,
    selectedStopover,
    setSelectedStopover,
    subTitle,
    visibleAddDataDrawer,
    setVisibleAddDataDrawer,
    labelAddDataDrawer,
    onSelectStopover,
    collapsibleCard,
    changeContentTrigger,
    selectedSubItemKey,
    setSelectedSubItemKey,
    pageContext,
    cardRenderer,
    fixedCardRenderer,
    labelPlural,
    queryPage,
    onChangePagination,
    setFilter,
    isFetchingStopoveresData,
    hasListDataForCurrentFilter,
    inputSearchPlaceholder,
    pageSize,
    maxListWindow,
  } = props;

  const [cardExpanded, setCardExpanded] = useState<number | undefined>(
    undefined
  );

  const listScrollRef = useRef<HTMLDivElement>(null);

  // Qual cobrança o painel deve mostrar, escrito no clique e lido depois do
  // `await`. A promessa do trigger resolve com o resultado do argumento dela, não
  // com o do último argumento disparado, então sem isto a resposta de um clique
  // anterior, mais lenta, sobrescreve a do clique atual.
  const requestedChargeId = useRef<number>();

  // A lista não é estado, é a resposta. O pedido é sempre `page=1` com
  // `page_size` do tamanho da janela que a rolagem já pediu, então toda resposta
  // é a lista inteira, ordenada e filtrada pelo servidor. Antes a lista era
  // acumulada aqui a partir de respostas de `page=N`: como cada resposta era uma
  // fatia sem contexto, o componente precisava de três sinalizadores para
  // adivinhar por que o dado tinha chegado, e a tela de Faturamento acabou com
  // dois escritores para o mesmo estado.
  //
  // O RTK Query retém o `data` do argumento anterior enquanto o novo carrega
  // (`data = isSuccess ? data : lastResult.data`), então crescer a janela não
  // pisca a tela nem perde a posição da rolagem.
  //
  // A rolagem também não pode mais travar: o comprimento é `min(janela, total)` e
  // a janela só cresce, então enquanto houver mais o comprimento cresce de fato,
  // que é a condição de o InfiniteScroll rearmar o `next`.
  const listedStopoveres = stopoveresData?.results || [];
  const totalStopoveres = stopoveresData?.count || 0;

  // A lista na tela é a da resposta anterior enquanto a nova não chega, e é o
  // `hasListDataForCurrentFilter` que separa as duas: falso significa que o que
  // está na tela responde a outro pedido.
  //
  // Só que isso vale igualmente para a rolagem, e os dois casos são opostos:
  // rolando, a lista na tela é o começo da lista pedida, então continua válida e
  // clicável; trocando de aba ou buscando, ela é de outro filtro e clicar nela
  // abre o painel de algo que já saiu da lista. O que separa os dois é a janela,
  // que a rolagem cresce e as outras duas devolvem à primeira página.
  //
  // Derivar isso do comprimento da lista não funciona, e era o defeito daqui:
  // antes de qualquer rolagem a janela pedida é exatamente uma página, então
  // "lista maior que a janela" era sempre falso e a busca e a troca de aba não
  // sinalizavam nada.
  const isFirstWindow = (queryPage || 1) === 1;
  const isListStale =
    !!isFetchingStopoveresData && !hasListDataForCurrentFilter && isFirstWindow;

  // A lista velha continua na tela enquanto a nova não chega, e a rolagem não
  // pode pedir mais dela: crescer a janela aqui é montar a janela da aba nova a
  // partir de um fim de lista que é de outro filtro, e ainda tira o `queryPage`
  // de 1, que é o que sustenta o "Carregando..." e os cards travados. Negar o
  // `hasMore` é o jeito de barrar sem efeito colateral: o InfiniteScroll só marca
  // o gatilho como consumido quando de fato chama o `next`, então nada fica
  // armado atrás, e ele volta a disparar sozinho assim que a lista nova chega.
  //
  // `isListStale`, e não "tem requisição em voo": crescer a janela também deixa o
  // `currentData` indefinido, e ali a lista na tela é o começo da que está sendo
  // pedida, então o `hasMore` tem que continuar de pé para o Skeleton do rodapé
  // aparecer.
  const hasMoreToList =
    !isListStale &&
    listedStopoveres.length < totalStopoveres &&
    listedStopoveres.length < maxListWindow;

  const [
    getStopoverDataById,
    { data: stopoverDataById, isFetching: isFetchingStopoverDataById },
  ] = useLazyGetStopoverQuery();

  const [
    getStopoverGroupByOperationDataById,
    {
      data: stopoverGroupByOperationDataById,
      isFetching: isFetchingStopoverGroupByOperationDataById,
    },
  ] = useLazyGetStopoverGroupbyOperationByIdQuery();

  const [
    getChargeDataById,
    { data: chargeDataById, isFetching: isFetchingChargeDataById },
  ] = useLazyGetChargeByIdQuery();

  const [
    getStorageGroupbyOperationById,
    {
      data: storageGroupbyOperationDataById,
      isFetching: isFetchingStorageGroupByOperationDataById,
    },
  ] = useLazyGetStorageGroupbyOperationByIdQuery();

  useEffect(() => {
    if (stopoverDataById)
      setSelectedStopover(formatStopoverToForm(stopoverDataById));
  }, [stopoverDataById]);

  useEffect(() => {
    if (stopoverGroupByOperationDataById)
      setSelectedStopover(
        formatStopoverToForm(stopoverGroupByOperationDataById)
      );
  }, [stopoverGroupByOperationDataById]);

  useEffect(() => {
    if (chargeDataById) setSelectedStopover(chargeDataById);
  }, [chargeDataById]);

  useEffect(() => {
    if (storageGroupbyOperationDataById)
      setSelectedStopover(storageGroupbyOperationDataById);
  }, [storageGroupbyOperationDataById]);

  // A aba e a busca montam outra lista, então a janela volta a uma página. Sem
  // isso a lista nova viria já do tamanho que a rolagem anterior deixou.
  const firstWindow = { page: 1, page_size: pageSize };

  // E a rolagem volta junto, porque devolver só o `page_size` não bastava. A
  // posição da rolagem mora no DOM, não no estado, então a lista de 40 itens
  // rolada até o fim continuava no fim: quando a resposta nova, de 10, encolhia o
  // conteúdo, o navegador prendia a rolagem no novo fundo, o InfiniteScroll lia
  // "chegou no fim" e pedia mais, e a janela subia de 10 para 20, 30, 40 sozinha.
  // O resultado era a aba nova reconstruindo exatamente a janela que a aba velha
  // tinha, e o `queryPage` saindo de 1 no meio do caminho, o que também derrubava
  // o "Carregando..." e o travamento dos cards.
  function resetListScroll() {
    if (listScrollRef.current) listScrollRef.current.scrollTop = 0;
  }

  function handleChangeTab(activeKey: string) {
    if (setFilter)
      if (
        ['STOPOVER', 'OPERATOR_AREA', 'INVOICING'].includes(pageContext || '')
      ) {
        setFilter((prev) => ({
          ...prev,
          ...firstWindow,
          status: activeKey,
          ...(activeKey === 'WAITING' && {
            order_by_delay_property: 'stopover_is_delayed_asc',
          }),
        }));
      } else if (pageContext === 'STAYMENT') {
        setFilter((prev) => ({
          ...prev,
          ...firstWindow,
          docking_status: activeKey,
        }));
      } else if (pageContext === 'STORAGE') {
        setFilter((prev) => ({
          ...prev,
          ...firstWindow,
          cargo_status: activeKey,
        }));
      }

    onChangePagination(1);
    resetListScroll();
    setSelectedStopover({} as StopoverType);
    setCardExpanded(undefined);
  }

  async function handleSelectStopover(stopover: StopoverType) {
    // Só altera se for selecionada uma escala diferente da q já ta
    if (
      pageContext === 'STORAGE' &&
      stopover.operation_id &&
      stopover.operation_id !== selectedStopover.operation?.id
    ) {
      const response = await getStorageGroupbyOperationById(
        stopover.operation_id
      );
      if ('data' in response && response.data !== undefined) {
        setSelectedStopover(response.data);
      }
      return;
    }
    // A seleção sai do retorno do trigger, e não do efeito que observa o
    // `chargeDataById`: o RTK Query liga `structuralSharing` por padrão, então
    // quando a resposta nova é igual à que já está em cache ele devolve o objeto
    // antigo, de propósito, para não provocar re-render. O efeito compara por
    // referência, logo ele não roda, e ninguém escreve o `selectedStopover`.
    //
    // Isso é o caminho comum depois de arquivar ou desarquivar em massa: o patch
    // invalida a tag Charge e rebusca esta cobrança enquanto ela ainda está
    // selecionada, a troca de aba zera a seleção, e o clique no mesmo card na aba
    // de destino traz resposta idêntica ao cache. O painel ficava vazio. Vale para
    // qualquer clique em card já visitado, só que arquivar é o que faz o mesmo
    // card aparecer nas duas abas.
    //
    // O efeito continua, e não é redundante: é ele que leva ao painel as rebuscas
    // que não vêm de clique, como a que o detector de transmissão dispara.
    if (
      pageContext === 'INVOICING' &&
      stopover.charge?.id !== selectedStopover.charge?.id
    ) {
      if (stopover.charge?.id) {
        requestedChargeId.current = stopover.charge.id;
        const response = await getChargeDataById(stopover.charge.id);
        if (requestedChargeId.current !== stopover.charge.id) return;
        if ('data' in response && response.data !== undefined) {
          setSelectedStopover(response.data);
        }
        return;
      }
    }
    if (
      pageContext === 'OPERATOR_AREA'
        ? stopover.operation_id !== selectedStopover.operation_id
        : stopover.id !== selectedStopover?.id
    ) {
      if (stopover.id) {
        if (pageContext === 'OPERATOR_AREA' && stopover.operation_id) {
          getStopoverGroupByOperationDataById(stopover.operation_id);
        } else if (stopoverDataById?.id === stopover.id) {
          setSelectedStopover(formatStopoverToForm(stopoverDataById));
        } else {
          getStopoverDataById(stopover.id);
        }
      }
      // Reseta o selectedSubItem para não mudar o conteúdo
      if (setSelectedSubItemKey) {
        setSelectedSubItemKey(null);
      }
    }
  }

  // Rolar não pede a página seguinte, pede a janela maior. `queryPage` deixou de
  // ser "qual página" e passou a ser "quantas páginas a rolagem já pediu".
  const loadMoreData = () => {
    const nextPage = (queryPage || 0) + 1;
    if (pageSize * nextPage > maxListWindow) return;

    onChangePagination(nextPage);
    if (setFilter)
      setFilter((prev) => ({
        ...prev,
        page: 1,
        page_size: pageSize * nextPage,
      }));
  };

  function handleFilterChange(search: string) {
    const sanitizedSearch =
      removeSpecialCharacters(search.trim(), undefined, /^\d{6}\/\d{4}$/) || '';
    onChangePagination(1);
    resetListScroll();
    if (setFilter)
      setFilter((prev) => ({
        ...prev,
        ...firstWindow,
        duv_or_vessel_name_or_imo_or_docking_place_name: sanitizedSearch,
        product_name: sanitizedSearch,
        complete_filter: sanitizedSearch,
      }));
  }

  return (
    <ScreenLayout title={title} subtitle={subTitle}>
      <LayoutContent>
        <LeftContent>
          <div className="search-stopover">
            <Input
              size="large"
              placeholder={
                inputSearchPlaceholder ||
                'Pesquisar embarcação / IMO / DUV / Escala mercante / Código da atracação'
              }
              type="text"
              prefix={
                <SearchOutlined
                  style={{ color: '#09d4ab', fontSize: '20px' }}
                />
              }
              allowClear
              style={{ marginBottom: '25px' }}
              onChange={debounce(
                (e) => handleFilterChange(e.target.value),
                400
              )}
            />
            {setVisibleAddDataDrawer && (
              <Button
                style={{ width: '250px' }}
                size="large"
                type="primary"
                block
                icon={<PlusOutlined />}
                onClick={() => setVisibleAddDataDrawer(!visibleAddDataDrawer)}
              >
                {labelAddDataDrawer}
              </Button>
            )}
          </div>
          <div>
            <Tabs
              defaultActiveKey={defaultActiveKey || ''}
              size="small"
              tabPosition="top"
              onChange={(e) => handleChangeTab(e)}
              type="card"
            >
              {tabs.map((tab) => {
                return (
                  <TabPane
                    disabled={
                      isLoadingStopoveresData || isFetchingStopoveresData
                    }
                    tab={tab.label}
                    key={tab.value}
                  />
                );
              })}
            </Tabs>
            <div className="amount">
              {isLoadingStopoveresData || isListStale ? (
                <span>Carregando...</span>
              ) : (
                <>
                  <span>{totalStopoveres}</span>
                  {labelPlural} NO TOTAL
                </>
              )}
            </div>
          </div>
          <StopoverCardWrapper>
            {isLoadingStopoveresData ? (
              <Skeleton active />
            ) : (
              <div
                id="scrollableDiv"
                ref={listScrollRef}
                style={{ height: '100%', overflow: 'auto', width: '100%' }}
              >
                <InfiniteScroll
                  dataLength={listedStopoveres.length}
                  next={loadMoreData}
                  hasMore={hasMoreToList}
                  loader={<Skeleton active />}
                  endMessage={
                    listedStopoveres.length >= maxListWindow &&
                    totalStopoveres > listedStopoveres.length ? (
                      <div className="list-window-limit">
                        Mostrando os primeiros {maxListWindow} de{' '}
                        {totalStopoveres}. Use a busca ou as abas para reduzir a
                        lista.
                      </div>
                    ) : null
                  }
                  scrollableTarget="scrollableDiv"
                >
                  {fixedCardRenderer ? (
                    <ListItem> {fixedCardRenderer} </ListItem>
                  ) : null}
                  <List
                    dataSource={listedStopoveres}
                    renderItem={(stopover: any) => (
                      <ListItem
                        key={stopover.id}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          paddingLeft: '5px',
                        }}
                      >
                        <StopoverCard
                          key={stopover.id}
                          onSelectStopover={
                            onSelectStopover || handleSelectStopover
                          }
                          expanded={cardExpanded}
                          stopover={stopover}
                          selectedStopover={selectedStopover}
                          collapsibleCard={collapsibleCard}
                          onSelectMenuItem={setSelectedSubItemKey}
                          pageContext={pageContext}
                          cardRenderer={cardRenderer}
                          disabled={isListStale}
                        />
                      </ListItem>
                    )}
                  />
                </InfiniteScroll>
              </div>
            )}
          </StopoverCardWrapper>
        </LeftContent>
        <RightContent>
          {isFetchingStopoverDataById ||
          isFetchingStopoverGroupByOperationDataById ||
          isFetchingChargeDataById ||
          isFetchingStorageGroupByOperationDataById ? (
            <FormWrapper>
              <PageContentPlaceholder
                message={
                  <Spin
                    spinning={
                      isFetchingStopoverDataById ||
                      isFetchingStopoverGroupByOperationDataById ||
                      isFetchingChargeDataById ||
                      isFetchingStorageGroupByOperationDataById
                    }
                    tip="Carregando..."
                  />
                }
              />
            </FormWrapper>
          ) : isEmpty(selectedStopover) ||
            (changeContentTrigger === 'SELECT_SUBITEM' &&
              selectedSubItemKey === null) ? (
            <FormWrapper>
              <PageContentPlaceholder />
            </FormWrapper>
          ) : (
            <FormWrapper key={selectedStopover?.id}>{children}</FormWrapper>
          )}
        </RightContent>
      </LayoutContent>
    </ScreenLayout>
  );
}
